
import { EMPLOYER_COMPLIANCE_HOME_HOTJAR_SITE_ID } from "@/constants/hotjar";
import { EMPLOYEE_ROLE, EMPLOYER_ROLE } from "@/constants/roles";
import {
  ARCHIVED_TASK_STATUS,
  COMPLETED_TASK_STATUS,
  EMPLOYEES_TASKS_TAB,
  EMPLOYER_TABS,
  EMPLOYER_TASKS_TABS,
  MY_TASKS_TAB,
} from "@/constants/tasks";
import { TNewTask_Table, TTask_Table } from "@/models/tasks";
import ComplianceBlock from "@/modules/Compliance/ComplianceWidgets/components/ComplianceBlock.vue";
import CompliantBlock from "@/modules/Compliance/ComplianceWidgets/components/CompliantBlock.vue";
import CompliantBlockRow from "@/modules/Compliance/ComplianceWidgets/components/CompliantBlockRow.vue";
import WidgetCandidateAssessment from "@/modules/Compliance/Widgets/WidgetCandidateAssessment.vue";
import WidgetHealthCareVisa from "@/modules/Compliance/Widgets/WidgetHealthCareVisa.vue";
import WidgetILR from "@/modules/Compliance/Widgets/WidgetILR.vue";
import WidgetSkilledWorkerVisa from "@/modules/Compliance/Widgets/WidgetSkilledWorkerVisa.vue";
import { RCandidateServices } from "@/modules/Services/RServices";
import { SMHotjar, SMQuestionnaire, SMTasks, SMUser } from "@/store";
import { Component, Vue, Watch } from "vue-property-decorator";

SMHotjar.initHotjar(EMPLOYER_COMPLIANCE_HOME_HOTJAR_SITE_ID);

const CASE_PRODUCT_TYPE = "case";

const VALUE_NAME = "name";
const VALUE_STATUS = "status";
const VALUE_SECTION = "complianceSection";
const VALUE_ACTION_BY = "actionBy";
const VALUE_DUE_DATE = "dueDate";

const ICONS = {
  documentTextOutline: "file-description",
  lock: "lock",
};

@Component({
  components: {
    CompliantBlock,
    CompliantBlockRow,
    ComplianceBlock,
    WidgetCandidateAssessment,
    WidgetSkilledWorkerVisa,
    WidgetHealthCareVisa,
    WidgetILR,
    WidgetGetSponsorLicence: () =>
      import("@/modules/Compliance/Widgets/WidgetGetSponsorLicence.vue"),
    CompliancePlaceholder: () => import("@/components/Placeholder/CompliancePlaceholder.vue"),
    Onboarding: () =>
      import("@/pages/Layout/views/Employer/views/Compliance/components/Onboarding.vue"),
    ComplianceWidget: () => import("@/modules/Compliance/ComplianceWidgets/ComplianceWidget.vue"),
    ReportChangePopup: () => import("@/components/Popups/ReportChangePopup.vue"),
  },
})
export default class Compliance extends Vue {
  get placeholderDescription() {
    if (SMUser.isSponsor && !SMUser.isSponsorConfirmation)
      return this.$tc("placeholder.descriptionSLNoSLNumber");

    return this.$tc("placeholder.description");
  }

  excludeStatuses = [COMPLETED_TASK_STATUS, ARCHIVED_TASK_STATUS];

  get items() {
    return SMTasks.employerTasksToDo;
  }

  openTask() {
    const items = this.tasks;

    const item = items[0];
    const path = `${SMUser.basePath}/tasks/${item.id}`;

    this.$router.push(path);
  }

  dialogs = {
    welcome: false,
    widgetSubmit: false,
  };

  get isSponsorConfirmation() {
    return SMUser.isSponsorConfirmation;
  }

  get isComplianceBlocked() {
    return SMUser.user?.is_compliance_blocked;
  }

  get reportChangeIcon() {
    return this.isComplianceBlocked ? ICONS.lock : ICONS.documentTextOutline;
  }

  get tasks() {
    return this.mainTasks;
  }

  get tasksCount() {
    return this.tasks.length;
  }

  async getTableTasks() {
    const { excludeStatuses } = this;

    const role = EMPLOYER_ROLE;
    const sideRole = EMPLOYEE_ROLE;

    await SMTasks.getMyTasks({ role: role, excludeStatuses });
    await SMTasks.getSideTasks({ role: sideRole, excludeStatuses });
  }

  get needsUpdate() {
    return SMQuestionnaire.needsUpdate;
  }

  @Watch("needsUpdate")
  update(val: boolean) {
    if (!val) return;

    this.getTableTasks();
  }

  mounted() {
    this.getTableTasks();
  }

  openSuccessPopup() {
    this.dialogs.widgetSubmit = true;
  }

  closeSuccessPopup() {
    this.dialogs.widgetSubmit = false;
  }

  closeWelcomePopup() {
    this.dialogs.welcome = false;
  }

  tabValues: string[] = EMPLOYER_TABS;

  tabNames = EMPLOYER_TASKS_TABS;

  get mainTasks() {
    return SMTasks.myTasks;
  }

  get mainTasksCompleted() {
    return SMTasks.myTasksCompleted;
  }

  get sideTasks() {
    return SMTasks.sideTasks;
  }

  get sideTasksCompleted() {
    return SMTasks.sideTasksCompleted;
  }

  get showTasksTable() {
    return true;
    // return !(SMUser.isEmployer && !this.mainTasks.length);
  }

  isReportChangePopup = false;

  openReportChangePopup() {
    this.isReportChangePopup = true;
  }

  closeReportChangePopup() {
    this.isReportChangePopup = false;
  }

  navigateTask(item: TNewTask_Table | TTask_Table) {
    const { id } = item;

    let path = `${SMUser.basePath}/tasks/${id}`;

    if ((item as TTask_Table)?.productType === CASE_PRODUCT_TYPE) {
      path = `/${RCandidateServices.path}/tasks/${id}`;
    }

    this.$router.push(path);
  }

  tabName = "";

  setTabName(value: string) {
    this.tabName = value;
  }

  get tableHeaders() {
    return [
      {
        text: this.$t("table.headers.name"),
        value: VALUE_NAME,
        span: 3,
        action: this.navigateTask,
      },
      {
        text: this.$t("table.headers.status"),
        value: VALUE_STATUS,
        filter: true,
        span: 0.5,
      },
      {
        text: this.$t("table.headers.complianceSection"),
        value: VALUE_SECTION,
      },
      {
        text: this.$t("table.headers.dueDate"),
        value: VALUE_DUE_DATE,
        info: this.$tc("table.hints.dueDate"),
        infoPosition: "left",
        span: 0.5,
      },
    ];
  }

  get tableWithActionByColumnHeaders() {
    return [
      {
        text: this.$t("table.headers.name"),
        value: VALUE_NAME,
        span: 2,
        action: this.navigateTask,
      },
      {
        text: this.$t("table.headers.status"),
        value: VALUE_STATUS,
        filter: true,
        span: 0.5,
      },
      {
        text: this.$t("table.headers.complianceSection"),
        value: VALUE_SECTION,
      },
      {
        text: this.$t("table.headers.actionBy"),
        value: VALUE_ACTION_BY,
        filter: true,
        filterSearch: true,
        span: 0.5,
      },
      {
        text: this.$t("table.headers.dueDate"),
        value: VALUE_DUE_DATE,
        info: this.$tc("table.hints.dueDate"),
        infoPosition: "left",
        span: 0.5,
      },
    ];
  }

  get headerValues() {
    if (this.tabName === MY_TASKS_TAB) {
      return this.tableHeaders;
    } else {
      return SMUser.isEmployer ? this.tableWithActionByColumnHeaders : this.tableHeaders;
    }
  }

  get headers() {
    const headerValues = this.headerValues as Array<{
      text: any;
      value: string;
      span?: number;
      action?: (item: TTask_Table | TNewTask_Table) => void;
      filter?: boolean;
      info?: any;
      sort?: boolean;
      sortBy?: any;
    }>;
    return headerValues.map((header) => ({
      label: header.text,
      value: header.value,
      sort: (header as any).sort || false,
      sortBy: (header as any).sortBy,
      filter: header.filter || false,
      info: header.info,
      infoPosition: (header as any).infoPosition || "right",
      filterSearch: (header as any).filterSearch || false,
      span: header.span,
      action: header.action,
    }));
  }

  get tabs() {
    return this.tabValues.map((value) => ({
      title: this.tabNames[value] || value,
      value,
      count: this.getTabCount(value),
    }));
  }

  getTabCount(tabValue: string) {
    if (tabValue === MY_TASKS_TAB) {
      return this.mainTasks.length;
    } else if (tabValue === EMPLOYEES_TASKS_TAB) {
      return this.sideTasks.length;
    }
    return 0;
  }

  get dataForTabs() {
    if (this.isComplianceBlocked) {
      const result: { [key: string]: TNewTask_Table[] } = {};
      this.tabValues.forEach((tabValue) => {
        result[tabValue] = [];
      });
      return result;
    }

    const result: { [key: string]: TNewTask_Table[] } = {};
    this.tabValues.forEach((tabValue) => {
      if (tabValue === MY_TASKS_TAB) {
        result[tabValue] = this.mainTasks;
      } else if (tabValue === EMPLOYEES_TASKS_TAB) {
        result[tabValue] = this.sideTasks;
      } else {
        result[tabValue] = [];
      }
    });
    return result;
  }

  loading = false;

  onTabSelect(tab: string) {
    this.setTabName(tab);
  }
}
